// i18n/useTranslation.js
// BuzzInApp — Hook de internacionalización
//
// Uso en cualquier componente:
//   const { t, lang, setLang } = useTranslation();
//   t("auth.welcomeTitle")                      → "Bienvenido a BuzzInApp"
//   t("share.clickCount", { n: 12 })            → "12 clicks recibidos"
//   t("claim.tooFarAway", { distance: 450 })    → "Estás a 450m del local..."

import { useState, useCallback } from "react";
import es from "./es";
import en from "./en";

const DICTIONARIES = { es, en };

// Detecta el idioma preferido del browser, con fallback a "es"
const detectLang = () => {
  const nav = (navigator.language || navigator.userLanguage || "es")
    .toLowerCase()
    .slice(0, 2);
  return nav === "en" ? "en" : "es";
};

/**
 * Resuelve un path tipo "auth.welcomeTitle" dentro del diccionario.
 * @param {object} dict
 * @param {string} path  dot-separated key
 * @returns {string|undefined}
 */
const resolvePath = (dict, path) =>
  path.split(".").reduce((acc, key) => acc?.[key], dict);

/**
 * Reemplaza tokens {variable} en un string.
 * @param {string} str   Ej: "Te faltan {n} clicks"
 * @param {object} vars  Ej: { n: 5 }
 * @returns {string}
 */
const interpolate = (str, vars = {}) =>
  str.replace(/\{(\w+)\}/g, (_, key) =>
    vars[key] !== undefined ? String(vars[key]) : `{${key}}`
  );

/**
 * Hook principal de i18n.
 * El idioma persiste en localStorage para mantenerlo entre sesiones.
 */
const useTranslation = () => {
  const [lang, setLangState] = useState(() => {
    try {
      return localStorage.getItem("buz_lang") || detectLang();
    } catch {
      return detectLang();
    }
  });

  const setLang = useCallback((newLang) => {
    if (!DICTIONARIES[newLang]) return;
    try { localStorage.setItem("buz_lang", newLang); } catch {}
    setLangState(newLang);
  }, []);

  /**
   * Traduce una clave con interpolación opcional.
   * @param {string} key   Ej: "auth.welcomeTitle" o "share.clickCount"
   * @param {object} vars  Variables a interpolar, ej: { n: 12 }
   * @returns {string}
   */
  const t = useCallback(
    (key, vars) => {
      const dict = DICTIONARIES[lang] ?? DICTIONARIES["es"];
      const value = resolvePath(dict, key);

      if (value === undefined) {
        // Fallback: intentar en el otro idioma
        const fallbackDict = lang === "es" ? DICTIONARIES["en"] : DICTIONARIES["es"];
        const fallback = resolvePath(fallbackDict, key);
        if (fallback !== undefined) return interpolate(String(fallback), vars);

        // Sin fallback: devolver la clave para detectar strings faltantes
        console.warn(`[i18n] Missing key: "${key}" (lang: ${lang})`);
        return key;
      }

      return interpolate(String(value), vars);
    },
    [lang]
  );

  return { t, lang, setLang };
};

export default useTranslation;
